import '@/assets/css/vuetify.scss'
import "@/assets/css/index.css";
import { createVuetify } from "vuetify";
import VueApexCharts from "vue3-apexcharts";

import { createApp } from "vue";
import { debounce } from "lodash";
import { setupI18n } from "./plugins/i18n";
import { instance } from "./api";
import { keycloak, loadToken, refreshToken, storeToken } from "@/utilities/keycloak";
import { useSettings } from "./store/settings";
import { useLocale } from "./store/locale";
import { useUI } from "./store/ui";
import { useUser } from "./store/user";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import { SessionStorage } from "./storage";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";

import "@mdi/font/css/materialdesignicons.css";
import * as hdColors from "@/theme/colors";
import { useContext } from "./store/context";
import { useRole } from "./store/role";
import "@/validation/index";
import type { KeycloakOnLoad } from "keycloak-js";
import { useNotification } from "./store/notification";
import StatsigPlugin, { STATSIG_KEY } from '@/plugins/statsig';
import { useNetwork } from './store/network';
import { StatsigClient } from '@statsig/js-client';
// import * as Sentry from "@sentry/vue";
// import type { Role } from "@/types/role";

const app = createApp(App);
setupI18n(app);

const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: "hdTheme",
    themes: {
      hdTheme: {
        dark: false,
        colors: {
          white: "#ffffff",
          "on-white": hdColors.grey["400"],
          primary: hdColors.primary["500"],
          "on-primary": "#ffffff",
          accent: hdColors.primary["100"],
          "on-accent": hdColors.primary["500"],
          secondary: hdColors.blue["100"],
          "on-secondary": hdColors.blue["500"],
          undo: "#F5F5F5",
          "on-undo": hdColors.grey["700"],
          success: "#22c55e",
          "on-success": "#ffffff",
          error: "#e5414b",
          "on-error": "#ffffff",
          danger: "#e5414b",
          "on-danger": "#ffffff",
          warning: "#f8ab51",
          "on-warning": "#ffffff",
          information: "#8bd5ec",
          "on-information": "#ffffff",
        },
      },
    },
  },
});

const tokenStorage = loadToken();

const keycloakParams: {
  onLoad: KeycloakOnLoad; // Modalità di caricamento
  silentCheckSsoRedirectUri: string; // URL per il controllo silenzioso della sessione (usato per verificare la sessione senza reindirizzare l'utente)
  checkLoginIframe: boolean; // controllo della sessione tramite iframe
  enableLogging: boolean; // log di debug
  token: string | null; // jwt
  refreshToken: string | null; // token di refresh
  silentCheckSsoFallback: boolean; // usa una strategia alternativa se il controllo SSO silenzioso fallisce -> Modern Browsers with Tracking Protection
} = {
  onLoad: "check-sso",
  silentCheckSsoRedirectUri: `${location.origin}/silent-check-sso.html`,
  checkLoginIframe: false,
  enableLogging: false,
  token: null,
  refreshToken: null,
  silentCheckSsoFallback: false,
};

if (tokenStorage) {
  keycloakParams.token = tokenStorage.token ?? null;
  keycloakParams.refreshToken = tokenStorage.refreshToken ?? null;
}

try {
  keycloak
    .init(keycloakParams)
    .then(async (authenticated) => {
      // Setting per user autenticato
      if (authenticated) {
        storeToken(); // Save token il localStorage
        refreshToken(); // Start token refresh loop
      }
    })
    .then(async () => {
      app.use(store);
      const settingsStore = useSettings();

      const UI = useUI();
      UI.getBreakpoint();
      const onResize = debounce(UI.getBreakpoint, 500);
      window.addEventListener("resize", onResize);
      window.addEventListener("orientationchange", onResize);
      instance.defaults.headers["X-Application"] = settingsStore.platformIdHeader;
      instance.defaults.headers["X-FrontendId"] = settingsStore.platformFrontendId;

      const userStore = useUser();
      const contextStore = useContext();
      const roleStore = useRole();
      const notificationStore = useNotification();
      // const networkStore = useNetwork();

      if (userStore.isLoggedIn) {
        try {
          userStore.setTokens(keycloak);

          await Promise.all([
            userStore.getCurrent(), 
            userStore.getRoles(), 
            roleStore.loadList(),
            notificationStore.setupAuthWatcher(),
            contextStore.loadApplicationList(),
            // networkStore.loadList()
          ]);

          if (userStore.hasSelectDesignerMacroRole) {
            await userStore.searchBankServiceProject();
          }

        } catch (error) {
          console.error(error);
        }
      }
      else{
        // Get application init info (logo, favicon etc..)
        await settingsStore.init();
      }
      
      settingsStore.authInitialized = true;
      console.log();
    })
    .then(async () => {
      const localeStore = useLocale();
      await localeStore.init();

      const locale: string = SessionStorage.getOr(["locale"], "it-IT");

      return await Promise.all([localeStore.changeLanguage(locale), localeStore.fetchLanguages()]);
    })
    .then(async () => {
      app.use(vuetify);
      app.use(VueApexCharts);
      app.use(router);

      const client = new StatsigClient(
        'client-Y9zzMIMweNXTFGQoOedJmogpif6rSitAmTQV2s84nPi',
        { userID: 'a-user' },
        {
          environment: { tier: import.meta.env.MODE },
        }
      )

      await client.initializeAsync() // ← qui Vue aspetta davvero

      app.provide(STATSIG_KEY, { client })

      // app.use(StatsigPlugin, {
      //   clientKey: 'client-Y9zzMIMweNXTFGQoOedJmogpif6rSitAmTQV2s84nPi', // dalla console Statsig → API Keys
      //   userId: 'a-user', // ID dell'utente corrente
      // })

      app.mount("#app");
    })
    .catch(console.error);
} catch (error) {
  console.error("Failed to initialize adapter:", error);
}
